import Box from '@mui/material/Box';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import { PageHeader } from './PageHeader';
import { fromAccounts, fromLayout, useAppDispatch } from '../../store';
import { LoadingProgress } from '../../layout';

const useStyles = makeStyles(theme => ({
  pageContainer: {
    overflow: 'scroll',
    height: '100%',
  },
  pageHeader: {
    padding: theme.spacing(4.5, 5, 3, 5),
    [theme.breakpoints.down('tablet')]: {
      padding: theme.spacing(3, 5),
    },
  },
  pageBody: {
    padding: theme.spacing(0, 5),
  },
  pageTopBanner: {
    padding: theme.spacing(3.5, 5, 3.5, 5),
    borderBottom: '1px solid #e5e7eb',
    // Hide the wrapper (and its divider) when the banner renders nothing —
    // avoids an empty bordered strip when the inner component returns null.
    '&:empty': {
      display: 'none',
    },
    [theme.breakpoints.down('tablet')]: {
      padding: theme.spacing(3, 5, 3, 5),
    },
  },
  pageTitle: {
    marginTop: 9,
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 11,
    fontSize: 14,
    color: '#5F5D75',
  },
  pageTitleAccountName: {
    fontWeight: 600,
    color: '#3C3A50',
  },
  pageTitleSeparator: {
    color: '#CDD6D2',
  },
}));

interface PageContainerProps {
  id: string;
  headerId: string;
  headerValue?: any;
  titleId?: string;
  titleValues?: any;
  topBanner?: ReactNode;
  background?: string;
  hideHeader?: boolean;
  fetchData: () => Promise<void>;
  children: ReactNode;
}

export const Page = (
  {
    id,
    headerId,
    headerValue,
    titleId,
    titleValues,
    topBanner,
    background,
    hideHeader,
    fetchData,
    children,
  }: PageContainerProps,
) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const account = useSelector(fromAccounts.selectSelectedAccount);
  const refetchData = useSelector(fromLayout.selectRefetchData);
  const [fetchingData, setFetchingData] = useState(false);

  useEffect(() => {
    dispatch(fromLayout.setLoading(false));
  }, []);

  useEffect(() => {
    if (refetchData) {
      setFetchingData(refetchData);
      fetchData()
        .catch(() => {
        })
        .finally(() => {
          setFetchingData(false);
          dispatch(fromLayout.setRefetchData(false));
        });
    }
  }, [refetchData]);

  useEffect(() => {
    const initiate = async () => {
      setFetchingData(true);
      await fetchData();
    };
    initiate()
      .finally(() => setFetchingData(false))
      .catch(() => {
      });
  }, [account]);

  return (
    <Paper
      id={`page-${id}-container`}
      className={classes.pageContainer}
      elevation={0}
      square
      sx={{ backgroundColor: background ?? '#F7F9F8' }}
    >
      {topBanner && (
        <Box
          id={`page-${id}-top-banner`}
          className={classes.pageTopBanner}
        >
          {topBanner}
        </Box>
      )}
      {!hideHeader && (
      <Box
        id={`page-${id}-header`}
        className={classes.pageHeader}
      >
        <PageHeader
          headerId={headerId}
          headerValue={headerValue}
        />
        {titleId
          ? (
            <Box id={`page-${id}-title`} className={classes.pageTitle}>
              <FormattedMessage id={titleId} values={titleValues} />
            </Box>
          )
          : (
            <Box id={`page-${id}-title`} className={classes.pageTitle}>
              <span className={classes.pageTitleAccountName}>{account?.name}</span>
              <span className={classes.pageTitleSeparator}>|</span>
              <span>
                <FormattedMessage
                  id="page.account.info"
                  values={{
                    accountId: account?.id,
                  }}
                />
              </span>
            </Box>
          )}
      </Box>
      )}
      <Box
        id={`page-${id}-content`}
        className={classes.pageBody}
        overflow="auto"
      >
        {fetchingData
          ? (
            <LoadingProgress fullscreen />
          )
          : children}
      </Box>
    </Paper>
  );
};
